import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import * as moment from 'moment-timezone';
import { AppService } from '../../../../../../../../shared/services/app.service';
import { RiskUpdate } from '../risk-register.models';
import { UPDATE_LABELS, updateIcon, UpdateIcon } from '../risk-register.util';

const ICON_GLYPHS: Record<UpdateIcon, string> = { ok: '✓', up: '▲', down: '▼', same: '', closed: '✓', conv: '→', rej: '✕', edit: '✎' };

/**
 * The risk's Updates tab, in the Submittals Discussion-tab timeline (markup
 * and styles from submittals-workflow.component). Newest first.
 */
@Component({
  selector: 'app-risk-updates-timeline',
  templateUrl: './risk-updates-timeline.component.html',
  styleUrls: ['./risk-updates-timeline.component.scss'],
  standalone: false,
})
export class RiskUpdatesTimelineComponent {
  @Input() updates: RiskUpdate[] = [];
  @Input() total = 0;
  @Input() projectTimezone = 'UTC';
  @Output() loadMore = new EventEmitter<void>();

  expanded = new Set<string>();
  readonly maxLength = 200;

  appService = inject(AppService);

  label(u: RiskUpdate): string { return `${UPDATE_LABELS[u.type] || u.type}:`; }
  icon(u: RiskUpdate): UpdateIcon { return updateIcon(u.type, u.data?.trend); }
  iconGlyph(u: RiskUpdate): string { return ICON_GLYPHS[this.icon(u)]; }
  hasScore(u: RiskUpdate): boolean {
    return (u.type === 'score_changed' || u.type === 'created' || u.type === 'proposed')
      && u.data?.score_after !== undefined && u.data?.score_after !== null;
  }
  actorName(u: RiskUpdate): string {
    return u.createdBy ? `${u.createdBy.first_name} ${u.createdBy.last_name}` : (u.data?.actor_name || 'Sitenotes');
  }
  initial(u: RiskUpdate): string { return (this.actorName(u) || '?').charAt(0); }
  changes(u: RiskUpdate): { field: string; before: unknown; after: unknown }[] {
    return Object.entries(u.data?.changes || {}).map(([field, [before, after]]) => ({ field: field.replace(/_/g, ' '), before, after }));
  }
  /** created / proposed / reassigned carry the owner; a reassignment to nobody reads "Unassigned". */
  ownerLabel(u: RiskUpdate): string | null {
    if (u.data?.owner_name) return u.data.owner_name;
    return u.type === 'reassigned' ? 'Unassigned' : null;
  }
  toggle(id: string): void {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }
  isExpanded(id: string): boolean { return this.expanded.has(id); }
  shouldTruncate(text: string): boolean { return !!text && text.length > this.maxLength; }
  truncated(text: string): string { return this.shouldTruncate(text) ? `${text.substring(0, this.maxLength)}...` : text; }
  formatInProjectTimezone(ts: string, format: string): string {
    if (!ts) return '';
    return moment.utc(ts).tz(this.projectTimezone || 'UTC').format(format);
  }
}
